if (!customElements.get('product-configurator')) {
  class ProductConfigurator extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      // Parse embedded JSON data
      const dataScript = this.querySelector('[data-configurator-data]');
      this.data = dataScript ? JSON.parse(dataScript.textContent) : null;
      if (!this.data) return;

      this.totalPriceEl = this.querySelector('[data-configurator-total]');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.selections = {}; // { categoryIndex: { variantId, price, title } }
      this.atcBtn = this.querySelector('[data-configurator-atc]');
      this.stickyBar = this.querySelector('[data-configurator-sticky]');
      this.stickyItemsEl = this.querySelector('[data-configurator-sticky-items]');

      // Bind click handlers to all option buttons
      this.querySelectorAll('.configurator__option').forEach((option) => {
        option.addEventListener('click', this.handleOptionClick.bind(this));
      });

      // Multistep panels ([Typ] → [Filter] → Produkte)
      this.querySelectorAll('.configurator__panel[data-layout="multistep"]').forEach((panel) => {
        this.initMultistepPanel(panel);
      });

      // Bind ATC button
      if (this.atcBtn) {
        this.atcBtn.addEventListener('click', this.handleATCClick.bind(this));
      }

      // Tab navigation
      this.querySelectorAll('.configurator__tab').forEach((tab) => {
        tab.addEventListener('click', this.handleTabClick.bind(this));
      });

      // Listen for variant changes on the main product
      document.addEventListener('on:variant:change', this.handleMainVariantChange.bind(this));

      // Intercept the main product form submission
      this.interceptProductForm();

      // Footer starts hidden until a selection is made
      this.updateFooter();

      // Scroll-triggered hairline animation + view tracking
      const cfgEl = this.querySelector('.configurator');
      if (cfgEl) {
        new IntersectionObserver((entries, obs) => {
          if (entries[0].isIntersecting) {
            cfgEl.classList.add('cfg-in-view');
            this.track('configurator_view');
            obs.disconnect();
          }
        }, { threshold: 0.35 }).observe(this);
      }
    }

    /**
     * Pushes a tracking event to the dataLayer. Consent gating happens in GTM,
     * so the push itself is always safe; it must never break the configurator.
     */
    track(eventName, params) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event: eventName }, params || {}));
      } catch (e) { /* no-op */ }
    }

    /**
     * Human-readable category label of a panel (e.g. "Leinwand").
     */
    categoryLabel(panel) {
      return panel ? (panel.dataset.categoryLabel || '') : '';
    }

    /**
     * Tracks a successful add-to-cart that included configurator accessories.
     * `source` distinguishes the configurator's own button from the main product form.
     */
    trackConfiguratorAtc(source) {
      const accessories = Object.values(this.selections);
      if (accessories.length === 0) return;
      this.track('configurator_add_to_cart', {
        configurator_source: source,
        configurator_items: accessories.length,
        configurator_products: accessories.map((s) => s.title).join(' | '),
        value: accessories.reduce((sum, s) => sum + s.price, 0) / 100,
        currency: 'EUR'
      });
    }

    /**
     * Handles tab click — switches visible panel.
     */
    handleTabClick(evt) {
      const clickedTab = evt.currentTarget;
      const tabIndex = clickedTab.dataset.tabIndex;

      // Deactivate all tabs
      this.querySelectorAll('.configurator__tab').forEach((tab) => {
        tab.classList.remove('is-active');
        tab.setAttribute('aria-selected', 'false');
      });

      // Activate clicked tab
      clickedTab.classList.add('is-active');
      clickedTab.setAttribute('aria-selected', 'true');

      // Hide all panels
      this.querySelectorAll('.configurator__panel').forEach((panel) => {
        panel.classList.remove('is-active');
      });

      // Show target panel
      const targetPanel = this.querySelector(`#configurator-panel-${tabIndex}`);
      if (targetPanel) {
        targetPanel.classList.add('is-active');
      }

      // Update "Schritt X von N"
      const stepEl = this.querySelector('[data-configurator-stepcurrent]');
      if (stepEl) {
        const tabs = Array.from(this.querySelectorAll('.configurator__tab'));
        stepEl.textContent = tabs.indexOf(clickedTab) + 1;
      }
    }

    /**
     * Guided flow — initialises one panel's state and bindings.
     */
    initMultistepPanel(panel) {
      const hasType = panel.dataset.hasType === 'true';
      panel._g = { type: hasType ? null : '__all__', filter: null, product: null, editing: null };

      panel.querySelectorAll('[data-type-tile]').forEach((tile) => {
        tile.addEventListener('click', this.guidedTypeClick.bind(this));
      });
      panel.querySelectorAll('[data-head]').forEach((btn) => {
        btn.addEventListener('click', this.guidedHeadClick.bind(this));
      });

      this.renderGuided(panel);
    }

    /**
     * Guided flow — a type card was clicked.
     */
    guidedTypeClick(evt) {
      const tile = evt.currentTarget;
      const panel = tile.closest('.configurator__panel');
      if (!panel || !panel._g) return;
      panel._g.type = tile.hasAttribute('data-type-none') ? '__none__' : (tile.dataset.typeKey || '');
      panel._g.filter = null;
      panel._g.editing = null;
      // "Ohne" always clears; single-select clears on type change; multi-select keeps prior picks
      if (panel._g.type === '__none__' || panel.dataset.multiSelect !== 'true') {
        this.clearCategorySelection(panel, panel.dataset.categoryIndex);
      }
      this.track('configurator_type_select', {
        configurator_category: this.categoryLabel(panel),
        configurator_type: tile.dataset.typeLabel || panel._g.type
      });
      this.renderGuided(panel);
    }

    /**
     * Guided flow — a filter (size) card was clicked.
     */
    guidedFilterClick(evt) {
      const card = evt.currentTarget;
      const panel = card.closest('.configurator__panel');
      if (!panel || !panel._g) return;
      panel._g.filter = card.dataset.value;
      panel._g.editing = null;
      if (panel.dataset.multiSelect !== 'true') {
        this.clearCategorySelection(panel, panel.dataset.categoryIndex);
      }
      const filterStep = card.closest('.cfg-step');
      this.track('configurator_filter_select', {
        configurator_category: this.categoryLabel(panel),
        configurator_filter: this.formatFilterValue(card.dataset.value, filterStep ? filterStep.dataset.filterSuffix : '')
      });
      this.renderGuided(panel);
    }

    /**
     * Guided flow — a step title was clicked → re-open that step (only if already completed).
     */
    guidedHeadClick(evt) {
      const el = evt.currentTarget;
      const panel = el.closest('.configurator__panel');
      const step = el.closest('.cfg-step');
      if (!panel || !panel._g || !step) return;
      const s = step.dataset.step;
      const g = panel._g;
      if (s === 'type' && g.type !== null) g.editing = 'type';
      else if (s === 'filter' && g.filter !== null && g.filter !== undefined) g.editing = 'filter';
      else return;
      this.renderGuided(panel);
    }

    /**
     * Guided flow — distinct filter values among products of a given type.
     */
    guidedFilterValues(panel, typeKey) {
      const vals = [];
      panel.querySelectorAll('.configurator__option').forEach((o) => {
        if (typeKey !== '__all__' && o.dataset.screenType !== typeKey) return;
        const v = (o.dataset.filter1 || '').trim();
        if (v && vals.indexOf(v) === -1) vals.push(v);
      });
      vals.sort((a, b) => {
        const na = parseFloat(a.replace(',', '.'));
        const nb = parseFloat(b.replace(',', '.'));
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
        return a.localeCompare(b, 'de');
      });
      return vals;
    }

    /**
     * Guided flow — formats a filter value (drops trailing .0, appends suffix).
     */
    formatFilterValue(v, suffix) {
      const n = parseFloat(String(v).replace(',', '.'));
      const isNum = /^[\d.,\s]+$/.test(v) && isFinite(n);
      return (isNum ? (Number.isInteger(n) ? `${n}` : String(n).replace('.', ',')) : v) + (suffix || '');
    }

    /**
     * Guided flow — sets a step's marker state (done wins over active).
     */
    setStep(step, done, active) {
      step.classList.toggle('is-done', !!done);
      step.classList.toggle('is-active', !!active && !done);
    }

    /**
     * Guided flow — renders the filter (size) cards for a step.
     */
    renderFilterCards(panel, step, values) {
      const wrap = step.querySelector('[data-configurator-filter]');
      if (!wrap) return;
      const suffix = step.dataset.filterSuffix || '';
      const current = panel._g.filter;
      wrap.innerHTML = '';
      values.forEach((v) => {
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'cfg-sizecard' + (current === v ? ' is-selected' : '');
        card.dataset.value = v;
        card.innerHTML =
          '<span class="cfg-sizecard-value">' + this.escapeHtml(this.formatFilterValue(v, suffix)) + '</span>' +
          '<span class="cfg-check-badge"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.4" aria-hidden="true"><path d="M4 12.5l5 5 11-12"/></svg></span>';
        card.addEventListener('click', this.guidedFilterClick.bind(this));
        wrap.appendChild(card);
      });
    }

    /**
     * Guided flow — reflects the whole panel state in the DOM.
     */
    renderGuided(panel) {
      const g = panel._g;
      if (!g) return;
      const hasTypeStep = panel.dataset.hasType === 'true';
      const hasFilterStep = panel.dataset.hasFilter === 'true';

      const typeChosen = hasTypeStep ? (g.type !== null) : true;
      const isNone = g.type === '__none__';
      const isScreen = hasTypeStep ? (typeChosen && !isNone) : true;
      const effType = hasTypeStep ? g.type : '__all__';

      let filterValues = [];
      let filterNeeded = false;
      if (hasFilterStep && isScreen) {
        filterValues = this.guidedFilterValues(panel, effType);
        filterNeeded = filterValues.length > 0;
      }
      const filterChosen = g.filter !== null && g.filter !== undefined;
      const filterDone = isScreen && (!filterNeeded || filterChosen);
      const showProducts = isScreen && filterDone;

      // ----- TYPE STEP -----
      const typeStep = panel.querySelector('.cfg-step[data-step="type"]');
      if (typeStep) {
        const expanded = !typeChosen || g.editing === 'type';
        this.setStep(typeStep, typeChosen, expanded);
        const chip = typeStep.querySelector('[data-chip]');
        const edit = typeStep.querySelector('[data-edit]');
        const expandEl = typeStep.querySelector('[data-expand]');
        if (expandEl) expandEl.hidden = !expanded;
        let selTile = null;
        typeStep.querySelectorAll('[data-type-tile]').forEach((t) => {
          const key = t.hasAttribute('data-type-none') ? '__none__' : (t.dataset.typeKey || '');
          const on = typeChosen && key === g.type;
          t.classList.toggle('is-selected', on);
          if (on) selTile = t;
        });
        if (typeChosen && !expanded) {
          if (chip) { chip.textContent = selTile ? (selTile.dataset.typeLabel || '') : ''; chip.hidden = false; }
          if (edit) edit.hidden = false;
        } else {
          if (chip) chip.hidden = true;
          if (edit) edit.hidden = true;
        }
      }

      // ----- FILTER STEP -----
      const filterStep = panel.querySelector('.cfg-step[data-step="filter"]');
      if (filterStep) {
        const chip = filterStep.querySelector('[data-chip]');
        const edit = filterStep.querySelector('[data-edit]');
        const expandEl = filterStep.querySelector('[data-expand]');
        const noteLock = filterStep.querySelector('[data-note-lock]');
        const noteSkip = filterStep.querySelector('[data-note-skip]');
        const hideChip = () => { if (chip) chip.hidden = true; if (edit) edit.hidden = true; };

        if (!typeChosen) {
          this.setStep(filterStep, false, false);
          if (expandEl) expandEl.hidden = true;
          hideChip();
          if (noteLock) noteLock.hidden = false;
          if (noteSkip) noteSkip.hidden = true;
        } else if (isNone) {
          this.setStep(filterStep, true, false);
          if (expandEl) expandEl.hidden = true;
          hideChip();
          if (noteLock) noteLock.hidden = true;
          if (noteSkip) noteSkip.hidden = false;
        } else {
          if (noteLock) noteLock.hidden = true;
          if (noteSkip) noteSkip.hidden = true;
          if (!filterNeeded) {
            this.setStep(filterStep, true, false);
            if (expandEl) expandEl.hidden = true;
            hideChip();
          } else {
            const expanded = !filterChosen || g.editing === 'filter';
            this.setStep(filterStep, filterChosen, expanded);
            this.renderFilterCards(panel, filterStep, filterValues);
            if (expandEl) expandEl.hidden = !expanded;
            if (filterChosen && !expanded) {
              const suffix = filterStep.dataset.filterSuffix || '';
              if (chip) { chip.textContent = this.formatFilterValue(g.filter, suffix); chip.hidden = false; }
              if (edit) edit.hidden = false;
            } else {
              hideChip();
            }
          }
        }
      }

      // ----- PRODUCTS STEP -----
      const prodStep = panel.querySelector('.cfg-step[data-step="products"]');
      if (prodStep) {
        const expandEl = prodStep.querySelector('[data-expand]');
        const noteLock = prodStep.querySelector('[data-note-lock]');
        const noteSkip = prodStep.querySelector('[data-note-skip]');
        if (isNone) {
          this.setStep(prodStep, false, false);
          if (expandEl) expandEl.hidden = true;
          if (noteLock) noteLock.hidden = true;
          if (noteSkip) noteSkip.hidden = false;
        } else if (showProducts) {
          if (noteLock) noteLock.hidden = true;
          if (noteSkip) noteSkip.hidden = true;
          if (expandEl) expandEl.hidden = false;
          panel.querySelectorAll('.configurator__option').forEach((o) => {
            const matchType = effType === '__all__' || o.dataset.screenType === effType;
            const matchFilter = !filterChosen || (o.dataset.filter1 || '').trim() === g.filter;
            o.style.display = (matchType && matchFilter) ? '' : 'none';
          });
          const hasSel = Object.values(this.selections).some((s) => String(s.categoryIndex) === String(panel.dataset.categoryIndex));
          this.setStep(prodStep, hasSel, !hasSel);
        } else {
          this.setStep(prodStep, false, false);
          if (expandEl) expandEl.hidden = true;
          if (noteLock) noteLock.hidden = false;
          if (noteSkip) noteSkip.hidden = true;
        }
      }
    }

    /**
     * Toggles an option's selected visual state.
     */
    setOptionSelected(option, on) {
      option.classList.toggle('is-selected', on);
      option.setAttribute('aria-pressed', on ? 'true' : 'false');
      const d = option.querySelector('.configurator__option-select-default');
      const a = option.querySelector('.configurator__option-select-active');
      if (d) d.style.display = on ? 'none' : '';
      if (a) a.style.display = on ? '' : 'none';
    }

    /**
     * Refreshes totals, sticky items, footer and the guided view after a selection change.
     */
    afterSelectionChange(panel) {
      this.updateTabStates();
      this.updateTotalPrice();
      this.updateStickyItems();
      this.updateFooter();
      if (panel && panel._g) this.renderGuided(panel);
    }

    /**
     * Removes all selected products of a category (visual + selection map).
     */
    clearCategorySelection(panel, categoryIndex) {
      panel.querySelectorAll('.configurator__option.is-selected').forEach((opt) => this.setOptionSelected(opt, false));
      let changed = false;
      Object.keys(this.selections).forEach((k) => {
        if (String(this.selections[k].categoryIndex) === String(categoryIndex)) {
          delete this.selections[k];
          changed = true;
        }
      });
      if (changed) {
        this.updateTabStates();
        this.updateTotalPrice();
        this.updateStickyItems();
        this.updateFooter();
      }
    }

    /**
     * Handles click on a product row. Single-select replaces within the category;
     * multi-select accumulates. Clicking a selected product removes it.
     */
    handleOptionClick(evt) {
      const option = evt.currentTarget;
      if (option.disabled) return;
      const panel = option.closest('.configurator__panel');
      if (!panel) return;

      const categoryIndex = panel.dataset.categoryIndex;
      const isMulti = panel.dataset.multiSelect === 'true';
      const variantId = option.dataset.variantId;

      // Toggle off an already-selected product
      if (option.classList.contains('is-selected')) {
        this.setOptionSelected(option, false);
        if (variantId) delete this.selections[variantId];
        this.afterSelectionChange(panel);
        return;
      }

      // Single-select: clear any other selected product in this category first
      if (!isMulti) {
        panel.querySelectorAll('.configurator__option.is-selected').forEach((opt) => {
          this.setOptionSelected(opt, false);
          const vid = opt.dataset.variantId;
          if (vid) delete this.selections[vid];
        });
      }

      // Select the clicked product
      this.setOptionSelected(option, true);
      if (variantId) {
        const imgEl = option.querySelector('.configurator__option-media img');
        const imageUrl = imgEl ? (imgEl.currentSrc || imgEl.src || '') : '';
        const price = parseInt(option.dataset.price, 10) || 0;
        const title = option.dataset.productTitle || '';
        this.selections[variantId] = { variantId, price, title, imageUrl, categoryIndex };
        this.track('configurator_product_select', {
          configurator_category: this.categoryLabel(panel),
          configurator_product: title,
          configurator_variant_id: variantId,
          price: price / 100,
          currency: 'EUR'
        });
      }
      this.afterSelectionChange(panel);
    }

    /**
     * Footer (summary bar) appears only when at least one accessory is selected.
     */
    updateFooter() {
      const footer = this.querySelector('[data-configurator-footer]');
      if (!footer) return;
      footer.classList.toggle('is-hidden', Object.keys(this.selections).length === 0);
    }

    /**
     * Updates tab visual states to show which categories have selections.
     */
    updateTabStates() {
      this.querySelectorAll('.configurator__tab').forEach((tab) => {
        const tabIndex = tab.dataset.tabIndex;
        const has = Object.values(this.selections).some((s) => String(s.categoryIndex) === String(tabIndex));
        tab.classList.toggle('has-selection', has);
      });
    }

    /**
     * Updates the sticky bar items to show selected accessories.
     */
    updateStickyItems() {
      if (!this.stickyItemsEl) return;

      // Remove old accessory items and separators (keep main product item)
      this.stickyItemsEl.querySelectorAll('.configurator__sticky-item--accessory').forEach((el) => el.remove());
      this.stickyItemsEl.querySelectorAll('.configurator__sticky-sep').forEach((el) => el.remove());
      this.stickyItemsEl.querySelectorAll('.configurator__sticky-item-separator').forEach((el) => el.remove());

      // Add selected accessories with + separator before each
      Object.values(this.selections).forEach((sel) => {
        const sep = document.createElement('div');
        sep.className = 'configurator__sticky-sep';
        sep.setAttribute('aria-hidden', 'true');
        sep.textContent = '+';
        this.stickyItemsEl.appendChild(sep);

        const item = document.createElement('div');
        item.className = 'configurator__sticky-item configurator__sticky-item--accessory';
        const imgHtml = `<div class="configurator__sticky-item-image">${
          sel.imageUrl ? `<img src="${sel.imageUrl}" alt="" loading="lazy">` : ''
        }</div>`;
        item.innerHTML = `
          ${imgHtml}
          <div class="configurator__sticky-item-namewrap">
            <span class="configurator__sticky-item-name">${this.escapeHtml(sel.title)}</span>
            <span class="configurator__sticky-item-price">${this.formatMoney(sel.price)}</span>
          </div>
        `;

        this.stickyItemsEl.appendChild(item);
      });

      this.updateStickyOverflow();
    }

    updateStickyOverflow() {
      if (!this.stickyItemsEl) return;
      const el = this.stickyItemsEl;
      const left = el.closest('.configurator__sticky-left');
      if (!left) return;

      const checkOverflow = () => {
        const scrollable = el.scrollWidth > el.clientWidth + 2;
        const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
        left.classList.toggle('has-overflow', scrollable && !atEnd);
      };

      checkOverflow();
      el.removeEventListener('scroll', el._overflowHandler);
      el._overflowHandler = checkOverflow;
      el.addEventListener('scroll', el._overflowHandler, { passive: true });
    }

    /**
     * Escapes HTML for safe insertion.
     */
    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    /**
     * Recalculates and displays the total price (main product + accessories).
     */
    updateTotalPrice() {
      let totalPrice = this.data.mainProductPrice;

      Object.values(this.selections).forEach((sel) => {
        totalPrice += sel.price;
      });

      if (this.totalPriceEl) {
        this.totalPriceEl.innerHTML = this.formatMoney(totalPrice);
      }
    }

    /**
     * Formats a price in cents to the shop's money format.
     */
    formatMoney(cents) {
      const format = this.data.currencyFormat;
      const amount = (cents / 100).toFixed(2);
      const amountWithComma = amount.replace('.', ',');
      const amountNoDecimals = Math.round(cents / 100).toString();
      const amountNoDecimalsWithComma = amountNoDecimals.replace('.', ',');

      // Add thousands separator
      const parts = amount.split('.');
      const withThousands = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      const amountWithPeriodAndComma = withThousands + ',' + parts[1];

      return format
        .replace('{{amount_with_comma_separator}}', amountWithComma)
        .replace('{{ amount_with_comma_separator }}', amountWithComma)
        .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithComma)
        .replace('{{ amount_no_decimals_with_comma_separator }}', amountNoDecimalsWithComma)
        .replace('{{amount_no_decimals}}', amountNoDecimals)
        .replace('{{ amount_no_decimals }}', amountNoDecimals)
        .replace('{{amount_with_apostrophe_separator}}', amount.replace(/\B(?=(\d{3})+(?!\d))/g, "'"))
        .replace('{{ amount_with_apostrophe_separator }}', amount.replace(/\B(?=(\d{3})+(?!\d))/g, "'"))
        .replace('{{amount}}', amount)
        .replace('{{ amount }}', amount);
    }

    /**
     * Handles main product variant changes.
     */
    handleMainVariantChange(evt) {
      const { variant } = evt.detail || {};
      if (variant) {
        this.data.mainProductPrice = variant.price;
        this.data.mainVariantId = variant.id;

        this.dataset.mainProductVariantId = variant.id;
        this.dataset.mainProductPrice = variant.price;

        // Update main price in sticky bar
        const mainPriceEl = this.querySelector('[data-main-price]');
        if (mainPriceEl) {
          mainPriceEl.innerHTML = this.formatMoney(variant.price);
        }

        this.updateTotalPrice();
      }
    }

    /**
     * Handles click on the configurator's own add-to-cart button.
     */
    async handleATCClick() {
      if (!this.atcBtn || this.atcBtn.getAttribute('aria-disabled') === 'true') return;

      this.atcBtn.setAttribute('aria-disabled', 'true');
      this.atcBtn.classList.add('is-loading');

      // Build items array: main product + selected accessories
      const productFormEl = document.querySelector('product-form');
      let mainVariantId = this.data.mainVariantId;
      let quantity = 1;

      if (productFormEl && productFormEl.form) {
        const formData = new FormData(productFormEl.form);
        mainVariantId = parseInt(formData.get('id'), 10) || mainVariantId;
        quantity = parseInt(formData.get('quantity'), 10) || 1;
      }

      const items = [{ id: mainVariantId, quantity }];

      const hidePrices = this.data.hideBundlePricesInCart;
      Object.values(this.selections).forEach((sel) => {
        const item = {
          id: parseInt(sel.variantId, 10),
          quantity: quantity,
          properties: { _source: 'konfigurator' }
        };
        if (hidePrices) {
          item.properties._bundle = 'true';
        }
        items.push(item);
      });

      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections,
            sections_url: window.location.pathname
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || data.message || response.status);
        }

        this.trackConfiguratorAtc('configurator_button');

        if (theme.settings.afterAtc === 'page') {
          window.location.href = theme.routes.cart;
          return;
        }

        // Update cart icon
        if (typeof ProductForm !== 'undefined' && ProductForm.updateCartIcon) {
          ProductForm.updateCartIcon(data);
        }

        // Open cart drawer
        if (this.cartDrawer) {
          setTimeout(() => {
            this.cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
          }, 300);
        }

        this.atcBtn.classList.add('is-success');
      } catch (error) {
        console.log(error);
        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        this.atcBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.atcBtn.classList.remove('is-loading');
          this.atcBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Intercepts the main product form to add accessories alongside the main product.
     */
    interceptProductForm() {
      const productFormEl = document.querySelector('product-form');
      if (!productFormEl || !productFormEl.form) {
        if (document.readyState !== 'complete') {
          window.addEventListener('load', () => this.interceptProductForm(), { once: true });
        }
        return;
      }

      if (theme.settings.afterAtc === 'no-js') return;

      const self = this;
      const originalSubmit = productFormEl.handleSubmit.bind(productFormEl);

      productFormEl.handleSubmit = async function (evt) {
        // If no accessories selected, use original behavior
        if (Object.keys(self.selections).length === 0) {
          return originalSubmit(evt);
        }

        evt.preventDefault();

        if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

        if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
          window.navigator.vibrate(100);
        }

        this.errorMsg = null;
        this.setErrorMsgState();

        this.submitBtn.setAttribute('aria-disabled', 'true');
        this.submitBtn.classList.add('is-loading');

        const formData = new FormData(this.form);
        const mainVariantId = parseInt(formData.get('id'), 10);
        const quantity = parseInt(formData.get('quantity'), 10) || 1;

        const items = [{ id: mainVariantId, quantity }];

        const hidePrices = self.data.hideBundlePricesInCart;
        Object.values(self.selections).forEach((sel) => {
          const item = {
            id: parseInt(sel.variantId, 10),
            quantity: quantity,
            properties: { _source: 'konfigurator' }
          };
          if (hidePrices) {
            item.properties._bundle = 'true';
          }
          items.push(item);
        });

        let sections = 'cart-icon-bubble';
        if (this.cartDrawer) {
          sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
        }

        try {
          const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
          if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
          const oldCartData = await oldCartResponse.json();

          const response = await fetch(theme.routes.cartAdd, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items,
              sections,
              sections_url: window.location.pathname
            })
          });

          const data = await response.json();

          if (!response.ok) {
            let error = typeof data.description === 'string'
              ? data.description : data.message;
            if (data.errors && typeof data.errors === 'object') {
              error = Object.entries(data.errors).map((item) => item[1].join(', '));
            }
            this.setErrorMsgState(error);
            throw new Error(response.status);
          }

          self.trackConfiguratorAtc('product_form');

          if (theme.settings.afterAtc === 'page') {
            setTimeout(() => {
              window.location.href = theme.routes.cart;
            }, 300);
          } else {
            ProductForm.updateCartIcon(data);

            setTimeout(() => {
              if (this.cartDrawer) {
                this.cartDrawer.renderContents(
                  data,
                  theme.settings.afterAtc === 'drawer'
                );
              } else if (window.location.pathname === theme.routes.cart) {
                const cartItems = document.querySelector('cart-items');
                if (cartItems) {
                  if (cartItems.dataset.empty === 'true') {
                    window.location.reload();
                  } else {
                    cartItems.refresh();
                  }
                }
              }
            }, 700);
          }

          const newCartResponse = await fetch(`${theme.routes.cart}.js`);
          if (!newCartResponse.ok) throw new Error(newCartResponse.status);
          const newCartData = await newCartResponse.json();

          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: mainVariantId
            }
          }));
        } catch (error) {
          console.log(error);
          this.dispatchEvent(new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: {
              error: this.errorMsg?.textContent
            }
          }));

          if (this.cartDrawer) this.cartDrawer.refresh(true);
        } finally {
          this.submitBtn.classList.add('is-success');
          this.submitBtn.removeAttribute('aria-disabled');
          setTimeout(() => {
            this.submitBtn.classList.remove('is-loading');
            this.submitBtn.classList.remove('is-success');
          }, 1400);
        }
      };
    }
  }

  customElements.define('product-configurator', ProductConfigurator);
}
